import { Checkbox, Chip, Stack, Typography, alpha, styled } from '@mui/material';
import type * as Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import type PreviewStore from '../../../AppData/_stores/PreviewStore';

interface Props {
	previews: Immutable.Seq.Indexed<PreviewStore>
	disabledPreviewIds: Immutable.List<number>
	onChangeMockup: (id: number, checked: boolean) => void
	legacy: boolean
	showHeading: boolean
}

const Thumbnail = styled('div', {
	shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
	backgroundColor: selected ? alpha(theme.palette.secondary.main, 0.08) : 'rgba(0, 0, 0, 0.06)',
	border: '2px solid',
	borderColor: selected ? theme.palette.secondary.main : 'transparent',
	borderRadius: 15,
	cursor: 'pointer',
	flexShrink: 0,
	overflow: 'hidden',
	position: 'relative',
	transition: 'background-color 150ms, border-color 150ms',

	'&:hover': {
		backgroundColor: selected ? alpha(theme.palette.secondary.main, 0.14) : 'rgba(0, 0, 0, 0.1)',
		borderColor: selected ? theme.palette.secondary.main : alpha(theme.palette.secondary.main, 0.35),
	},

	'img': {
		filter: selected ? 'none' : 'grayscale(0.15)',
		opacity: selected ? 1 : 0.58,
	}
}));

const ThumbnailImg = styled('img')(({ theme }) => ({
	backgroundColor: theme.palette.common.white,
	display: 'block',
	height: 106,
	objectFit: 'contain',
	transition: 'filter 150ms, opacity 150ms',
	width: 118,

	[theme.breakpoints.down('sm')]: {
		height: 96,
		width: 108,
	}
}));

export default function ProductMockupGroup(props: Props) {
	const [t] = useTranslation('account-v2');

	return <Stack spacing={1}>
		{props.showHeading ? <Typography variant="subtitle2" component="h4">
			{props.legacy ? t('Legacy mockups') : t('Current mockups')}
		</Typography> : null}
		{props.legacy ? <Typography variant="body2" color="text.secondary">
			{t('These mockups are used by older designs. When publishing, only mockups available for each design are included.')}
		</Typography> : null}
		<Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
			{props.previews.map(preview => {
				const checked = !props.disabledPreviewIds.includes(preview.get('id'));

				return <Thumbnail
					key={preview.get('id')}
					selected={checked}
					onClick={() => props.onChangeMockup(preview.get('id'), !checked)}
				>
					<ThumbnailImg src={preview.get('url')} alt="" />
					<Checkbox
						color="secondary"
						checked={checked}
						inputProps={{ 'aria-label': preview.get('slug') }}
						onClick={(e) => e.stopPropagation()}
						onChange={(e, checked) => props.onChangeMockup(preview.get('id'), checked)}
						sx={{ position: 'absolute', left: 0, top: 0 }}
					/>
					{props.legacy ? <Chip
						label={t('Legacy')}
						size="small"
						sx={{ position: 'absolute', bottom: 6, right: 6, height: 20, fontSize: '0.65rem', backgroundColor: 'background.paper' }}
					/> : null}
				</Thumbnail>;
			})}
		</Stack>
	</Stack>;
}
